import { Component, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { SeoService } from 'src/app/services/seo/seo.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EstatusPostulacionType } from 'src/app/enums/EstatusPostulacionType.enum';
import { RolesType } from 'src/app/enums/RolesType.enum';
import { Curso } from 'src/app/interfaces/curso';
import { JwtResponse } from 'src/app/interfaces/jwt';
import { PerfilCandidato } from 'src/app/interfaces/perfil';
import { Postulacion, PostulacionVacante } from 'src/app/interfaces/postulacion';
import { Ciudad, Estado } from 'src/app/interfaces/ubicaciones';
import { VacanteDTO } from 'src/app/interfaces/vacantes';
import { PostulacionService } from 'src/app/services/postulacion/postulacion.service';
import { UbicacionService } from 'src/app/services/ubicacion/ubicacion.service';
import { UsuarioService } from 'src/app/services/usuario/usuario.service';
import { VacantesService } from 'src/app/services/vacante/vacantes.service';
import { CandidatoCvService } from 'src/app/services/candidato-cv/candidato-cv.service';
import { environment } from 'src/environments/environment';
import { textChangeRangeIsUnchanged } from 'typescript';
import { ModalMessageComponent } from '../../common/modalmessage/modalmessage.component';

@Component({
  selector: 'app-job-details',
  templateUrl: './job-details.component.html',
  styleUrls: ['./job-details.component.scss']
})
export class JobDetailsComponent implements OnInit, OnDestroy {

  usuario: JwtResponse;
  detalleVacante: VacanteDTO;
  role: string;
  estados: Estado[];
  ciudades: Ciudad[];
  estado: Estado;
  ciudad: Ciudad;
  cursos: Curso[];
  imgCurso: string;
  baseCurso: string;
  postulando = false;
  yaPostulado = false;
  cargandoPostulacion = true;
  errorMsg = '';
  externalUrl = '';
  vacanteCerrada = false;

  constructor(private title: Title, private meta: Meta, private vacantesService: VacantesService,
    private activatedRoute: ActivatedRoute, private ubicacionService: UbicacionService,
    private router: Router, private modalService: NgbModal, private postulacionService: PostulacionService,
    private usuarioService: UsuarioService, private seo: SeoService,
    private cvService: CandidatoCvService, private location: Location) {
    this.usuario = JSON.parse(localStorage.getItem('currentUser'));
    this.role = null;
    this.detalleVacante = null as VacanteDTO;
    this.estado = {} as Estado;
    this.ciudad = {} as Ciudad;
    this.role = (this.usuario ? this.usuario.role : null);
    this.imgCurso = environment.baseCursoImg;
    this.baseCurso = environment.baseCurso;
  }

  /** Regresa al historial real del navegador — así se restaura la lista de /vacantes con el filtro/página que ya tenía. */
  regresar(): void {
    if (this.location) {
      this.location.back();
    } else {
      this.router.navigate(['/vacantes']);
    }
  }

  ngOnInit(): void {
    const param: string = this.activatedRoute.snapshot.params.vacante;
    const isNumericParam = /^\d+$/.test(param);
    const urlVacante: string = isNumericParam ? null : param;
    const idVacante: number = isNumericParam
      ? parseInt(param, 10)
      : (JSON.parse(localStorage.getItem('idVacante')) ?? 0);
    if (urlVacante || idVacante) {
      this.cargarDetallesVacante(idVacante, urlVacante);
    } else {
      this.router.navigate(['/vacantes']);
      this.abrirModalMessage("Error", "No existe la vacante solicitado.");
    }
  }

  private agregarMetaData(): void {
    const v = this.detalleVacante;
    const rawLogo = (v as any).logo ?? (v as any).logoEmpresa;
    const formattedLogo = rawLogo
      ? (rawLogo.startsWith('http') ? rawLogo : (environment.baseArchivos + rawLogo))
      : undefined;

    // Fecha real de publicación (no la fecha de hoy)
    const rawFecha = (v as any).fechaCreado ?? (v as any).createdAt;
    const fechaPublicacionDate = rawFecha ? new Date(rawFecha) : new Date();
    const fechaPublicacion = fechaPublicacionDate.toISOString().split('T')[0];

    // Google exige validThrough para poder despublicar la vacante de Google for Jobs
    // automáticamente al expirar — usa la misma ventana que el cierre automático del
    // backend (VacanteCloserScheduler, AppConfigHolder.mesesVacantes, default 3 meses).
    const fechaExpiracionDate = new Date(fechaPublicacionDate);
    fechaExpiracionDate.setMonth(fechaExpiracionDate.getMonth() + 3);
    const fechaExpiracion = fechaExpiracionDate.toISOString().split('T')[0];

    // Ciudad desde el estado/ciudad resuelto
    const ciudadStr = this.ciudad?.nombre
      ?? this.estado?.nombre
      ?? (v as any).ciudad
      ?? undefined;

    this.seo.setJobPosting({
      titulo:           v.nombre,
      empresa:          this.getNombreEmpresa(),
      descripcion:      v.descripcion,
      descripcionCorta: this.cortarTextoDescripcion(),
      fechaPublicacion,
      fechaExpiracion,
      modalidad:        v.modalidadEmpleo ?? v.tipoContrato ?? '',
      ciudad:           ciudadStr,
      salarioMin:       v.salarioMin,
      salarioMax:       v.salarioMax,
      slug:             v.urlVacante ?? String(v.idVacante),
      logo:             formattedLogo,
      tecnologias:      (v as any).tecnologias ?? [],
    });
  }

  private getNombreEmpresa(): string {
    // VacanteDTO no expone nombreEmpresa directamente; usamos un fallback genérico
    return (this.detalleVacante as any).nombreEmpresa ?? 'TecGurus';
  }

  ngOnDestroy(): void {
    // Limpia el JSON-LD al salir de la página
    this.seo.setDefault();
  }

  async cargarDetallesVacante(idVacante: number, urlVacante: string): Promise<any> {
    try {
      const response = await this.vacantesService.obtenerDetallesVacante(idVacante, urlVacante);
      this.detalleVacante = response.data;
      if (response.data != null) {
        this.obtenerEstadoVacante();
        this.obtenerCiudadVacante(this.detalleVacante.idEstado);
        this.obtenerCursosPorVacante(this.detalleVacante.idCategoria);
        this.agregarMetaData();
        if (this.usuario?.role === 'candidato') {
          // Check localStorage first for instant result
          const applied = JSON.parse(localStorage.getItem('applied_vacantes') || '[]');
          if (applied.includes(this.detalleVacante.idVacante)) {
            this.yaPostulado = true;
          } else {
            const postulaciones = await this.obtenerPostulacion(this.usuario.user.idUsuario, this.detalleVacante.idVacante);
            this.yaPostulado = Array.isArray(postulaciones) && postulaciones.length > 0;
          }
        }
        this.cargandoPostulacion = false;
      }else {
        this.cargandoPostulacion = false;
        // Si viene de GuruMatch, no redirigir — mostrar mensaje y enlace externo
        const state = history.state as any;
        if (state?.scrapedJobId && state?.urlOriginal) {
          this.errorMsg = 'Esta vacante aún no está disponible en TG Empleos.';
          this.externalUrl = state.urlOriginal;
        } else {
          this.router.navigate(['/vacantes']);
        }
      }
    } catch (error) {
      this.cargandoPostulacion = false;
      // 410 Gone — vacante cerrada. El backend devuelve la VacanteDTO en el body.
      // Renderizamos página archivo: noindex + canonical a /vacantes para preservar SEO ~60d.
      const httpErr = error as any;
      if (httpErr?.status === 410 && httpErr?.error?.data) {
        this.detalleVacante = httpErr.error.data;
        this.vacanteCerrada = true;
        this.seo.setNoIndex();
        return;
      }
      console.error("Error al cargar detalles", error);
      const state = history.state as any;
      if (state?.scrapedJobId && state?.urlOriginal) {
        this.errorMsg = 'Esta vacante no se encontró en TG Empleos. Puedes verla en la fuente original.';
        this.externalUrl = state.urlOriginal;
      }
    }
  }

  private cortarTextoDescripcion () : string {
      let nuevaDescripcion: string;

      if(this.detalleVacante.descripcion.length > 90){
        nuevaDescripcion = this.detalleVacante.descripcion.substring(0,90);
        nuevaDescripcion += "..."
      }else {
        nuevaDescripcion = this.detalleVacante.descripcion;
      }

      return nuevaDescripcion;
  }

  async obtenerEstadoVacante() {
    try {
      const response = await this.ubicacionService.obtenerEstados();
      this.estados = response.data;
      if (this.estados) {
        this.estado = this.estados.find(estado => estado.id === this.detalleVacante.idEstado);
      }
    } catch (error) {
      console.error("Error en cargar los estados.", error);
    }
  }

  async obtenerCiudadVacante(idEstado: number) {
    try {
      const response = await this.ubicacionService.obtenerCiudadesPorEstado(idEstado);
      this.ciudades = response.data;
      if (this.ciudades) {
        this.ciudad = this.ciudades.find(ciudad => ciudad.claveCiudad === this.detalleVacante.idCiudad);
      }
    } catch (error) {
      console.error("Error en cargar los estados.", error);
    }
  }

  async postularseVacante(vacante: VacanteDTO) {
    if (this.postulando || this.yaPostulado) return;
    if (this.usuario.role === RolesType.CANDIDATOS) {
      const candidato: PerfilCandidato = await this.obtenerPerfilCandidato(this.usuario.user.idUsuario);

      if (candidato && vacante) {

        // Check for active CV before applying
        try {
          const cvsRes = await this.cvService.list(this.usuario.user.idUsuario).toPromise();
          const cvs = cvsRes?.data ?? [];
          const tieneActivo = cvs.some((c: any) => c.isActive);
          if (!tieneActivo) {
            this.abrirModalMessage('CV requerido', 'Necesitas subir y activar un CV antes de postularte. Ve a "Mis CVs" para hacerlo.');
            return;
          }
        } catch (e) { /* allow through if CV service fails */ }

        const postulacion: Postulacion = {
          idCandidato: candidato.idUsuario,
          idVacante: vacante.idVacante,
          nombrePostulante: candidato.nombre.trim(),
          appaternoPostulante: candidato.apellidos.substring(0, candidato.apellidos.lastIndexOf(' ')).trim(),
          apmaternoPostulante: candidato.apellidos.substring(candidato.apellidos.lastIndexOf(' ')).trim(),
          telefonoPostulante: '+' + candidato.claveTelefono + candidato.telefono,
          ciudadPostulante: candidato.pais,
          correoPostulante: candidato.correo,
          estatus: EstatusPostulacionType.PEDIENTE
        };

        const postulaciones: PostulacionVacante[] = await this.obtenerPostulacion(candidato.idUsuario, vacante.idVacante);

        if (postulaciones.length == 0) {
          this.postulando = true;
          this.postulacionService.registrarPostulacion(postulacion).pipe(
            catchError(error => {
              this.postulando = false;
              let mensajeError = '';
              if (error.status === 0) {
                mensajeError = 'Error: se ha producido un error: ' + error.error + '\n';
                mensajeError += `Http error: ${error.message}`;
                console.error('se ha producido un error:', error.error);
              } else {
                mensajeError = `Error (código ${error.error.statusCode}): mensaje de error: ${error.error.message}.\n`;
                mensajeError += `Http error: ${error.message}`;
                console.error(`Error en el API ${error.status}, cuerpo: `, error.error);
              }
              this.abrirModalMessage("Error", mensajeError);
              return throwError(mensajeError);
            })
          ).subscribe(result => {
            this.postulando = false;
            if (result.success) {
              this.yaPostulado = true;
              // Persist to localStorage so the button stays disabled on re-visit
              const applied = JSON.parse(localStorage.getItem('applied_vacantes') || '[]');
              applied.push(this.detalleVacante.idVacante);
              localStorage.setItem('applied_vacantes', JSON.stringify(applied));
              let partes: string[] = [];
              const nombre = [vacante.nombreContacto, vacante.appaternoContacto, vacante.apmaternoContacto]
                .filter(p => p && p !== 'null').join(' ');
              if (nombre) partes.push('Contacto: ' + nombre);
              if (vacante.cargoContacto && vacante.cargoContacto !== 'null') partes.push('Cargo: ' + vacante.cargoContacto);
              if (vacante.correoContacto && vacante.correoContacto !== 'null') partes.push('Correo: ' + vacante.correoContacto);
              if (vacante.direccion && vacante.direccion !== 'null') partes.push('Dirección: ' + vacante.direccion);
              const mensaje = partes.length > 0 ? partes.join('\n') : '¡Tu postulación fue enviada con éxito!';
              this.abrirModalMessage("Postulación enviada", mensaje);
              this.router.navigate(['/vacantes']);
            } else {
              this.abrirModalMessage("Error", "No se ha creado la postulación correctamente");
              console.error('Error al crear la postulación del candidato: ', result.messageError);
            }
          });
        } else {
          this.abrirModalMessage("Error", "Solo te puedes postular sola una vez.");
        }
      }
    }
  }

  private async obtenerPerfilCandidato(idCandidato: number) {
    try {
      const response = await this.usuarioService.obtenerDetallesCandidatoPorId(idCandidato);
      return response.data;
    } catch (error) {
      console.error("Error en obtener el perfil del candidato con el id: " + this.usuario.user.idUsuario, error);
    }
  }

  private async obtenerPostulacion(idCandidato: number, idVacante: number) {
    try {
      const response = await this.postulacionService.obtenerPosutlacionPorCandidatoYVacante(idCandidato, idVacante);
      return response.data;
    } catch (error) {
      console.error("Error al obtener la postulacion", error);
    }
  }

  private async obtenerCursosPorVacante(idCategoria: number) {
    try {
      const response = await this.vacantesService.obtenerCursosRelacionados(idCategoria);
      this.cursos = response.data;
    } catch (error) {
      console.error("Error obtener los cursos relacionado por vacante", error);
    }
  }

  abrirModalMessage(modalTitle: string, modalMessage: string) {
    const modalRef = this.modalService.open(ModalMessageComponent, { centered: true });
    modalRef.componentInstance.modalTitle = modalTitle;
    modalRef.componentInstance.modalMessage = modalMessage;
  }

  irAVacanteOriginal(): void {
    const url = (this.detalleVacante as any).scrapeUrlOriginal;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  optionsSlides: OwlOptions = {
    loop: true,
    nav: true,
    dots: false,
    autoplayHoverPause: true,
    autoplay: true,
    margin: 30,
    navText: [
      "<i class='icofont-arrow-left'></i>",
      "<i class='icofont-arrow-right'></i>"
    ],
    responsive: {
      0: {
        items: 1
      },
      576: {
        items: 2
      },
      768: {
        items: 3
      },
      1200: {
        items: 4
      }
    }
  }
}

